
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { LoadingState, ErrorState, EmptyState, PageHeader } from "@/components/features/states";
import { toast } from "sonner";
import {
  Smartphone,
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  Wallet,
} from "lucide-react";
import type { MobileMoneyProvider } from "@/lib/types";

const PROVIDERS: { code: MobileMoneyProvider; flag: string }[] = [
  { code: "dmoney", flag: "🇩🇯" },
  { code: "ebirr", flag: "🇪🇹" },
  { code: "zaad", flag: "🇸🇴" },
  { code: "waafi", flag: "🇸🇴" },
];

export function MobileMoneyView() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [linkOpen, setLinkOpen] = useState(false);
  const [provider, setProvider] = useState<MobileMoneyProvider>("dmoney");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [pin, setPin] = useState("");
  const [linking, setLinking] = useState(false);

  const { data: accountsData, isLoading, error } = useQuery({
    queryKey: ["mobile-money-accounts"],
    queryFn: () => api.mobileMoney.list(),
  });
  const { data: txnData } = useQuery({
    queryKey: ["mobile-money-transactions"],
    queryFn: () => api.mobileMoney.transactions({ limit: 10 }),
  });

  const accounts = accountsData?.data?.accounts ?? [];
  const transactions = txnData?.data?.transactions ?? [];

  const link = async () => {
    if (!accountNumber.trim()) return toast.error(t("mobileMoney.accountNumber"));
    if (pin.length < 4) return toast.error(t("mobileMoney.enterPin", { provider: providerLabel(provider) }));
    setLinking(true);
    const res = await api.mobileMoney.link({
      provider,
      accountNumber: accountNumber.trim(),
      accountName: accountName.trim() || undefined,
      pin,
    });
    setLinking(false);
    if (!res.ok) return toast.error(res.error || t("errors.generic"));
    qc.invalidateQueries({ queryKey: ["mobile-money-accounts"] });
    setLinkOpen(false);
    setAccountNumber("");
    setAccountName("");
    setPin("");
    toast.success(t("mobileMoney.linkSuccess"));
  };

  const unlink = async (id: string) => {
    const res = await api.mobileMoney.unlink(id);
    if (!res.ok) return toast.error(res.error || t("errors.generic"));
    qc.invalidateQueries({ queryKey: ["mobile-money-accounts"] });
    toast.success(t("mobileMoney.unlinkSuccess"));
  };

  const providerLabel = (p: string) =>
    p === "dmoney" ? t("mobileMoney.dmoney") : p === "ebirr" ? t("mobileMoney.ebirr") : p === "zaad" ? t("mobileMoney.zaad") : t("mobileMoney.waafi");
  const providerDesc = (p: string) =>
    p === "dmoney" ? t("mobileMoney.dmoneyDesc") : p === "ebirr" ? t("mobileMoney.ebirrDesc") : p === "zaad" ? t("mobileMoney.zaadDesc") : t("mobileMoney.waafiDesc");

  if (isLoading) return <LoadingState label={t("common.loading")} />;
  if (error) return <ErrorState message={t("errors.generic")} onRetry={() => qc.invalidateQueries({ queryKey: ["mobile-money-accounts"] })} />;

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeader title={t("mobileMoney.title")} subtitle={t("mobileMoney.subtitle")} />

      {/* Demo notice */}
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
        {t("mobileMoney.demoSimulated")}
      </div>

      {/* Linked accounts */}
      <div className="space-y-3">
        {accounts.length === 0 ? (
          <EmptyState
            icon={<Wallet className="h-8 w-8" />}
            title={t("mobileMoney.noAccounts")}
            description={t("mobileMoney.noAccountsDesc")}
            action={
              <Button size="sm" onClick={() => setLinkOpen(true)}>
                <Plus className="mr-1 h-4 w-4" /> {t("mobileMoney.linkAccount")}
              </Button>
            }
          />
        ) : (
          accounts.map((acc) => (
            <Card key={acc.id}>
              <CardContent className="flex items-center gap-3 p-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Smartphone className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{providerLabel(acc.provider)}</p>
                    <Badge variant="secondary" className="text-[10px]">
                      {t("mobileMoney.linked")}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {acc.accountNumber}
                    {acc.accountName ? ` · ${acc.accountName}` : ""}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{providerDesc(acc.provider)}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-destructive"
                  onClick={() => unlink(acc.id)}
                  aria-label={t("mobileMoney.unlink")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {accounts.length > 0 && (
        <Button variant="outline" className="w-full" onClick={() => setLinkOpen(true)}>
          <Plus className="mr-1 h-4 w-4" /> {t("mobileMoney.linkAccount")}
        </Button>
      )}

      {/* Recent transactions */}
      {transactions.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-semibold">{t("mobileMoney.title")}</p>
          <Card>
            <CardContent className="divide-y divide-border/60 p-0">
              {transactions.slice(0, 8).map((txn) => (
                <div key={txn.id} className="flex items-center gap-3 p-3">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full ${
                      txn.status === "success"
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                        : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                    }`}
                  >
                    {txn.status === "success" ? (
                      <ArrowUpRight className="h-4 w-4" />
                    ) : (
                      <AlertCircle className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {providerLabel(txn.provider)} · <span className="font-bold text-gold">{txn.amount} {txn.currency}</span>
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {new Date(txn.createdAt).toLocaleString()} · {txn.reference}
                    </p>
                  </div>
                  <Badge
                    variant={txn.status === "success" ? "default" : "destructive"}
                    className="text-[10px]"
                  >
                    {txn.status}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Link dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("mobileMoney.linkAccount")}</DialogTitle>
            <DialogDescription>{t("mobileMoney.subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("mobileMoney.provider")}</Label>
              <div className="grid grid-cols-3 gap-2">
                {PROVIDERS.map((p) => (
                  <button
                    key={p.code}
                    onClick={() => setProvider(p.code)}
                    className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-xs transition ${
                      provider === p.code ? "border-primary bg-primary/5" : "border-border"
                    }`}
                  >
                    <span className="text-xl">{p.flag}</span>
                    <span className="font-semibold">{providerLabel(p.code)}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">{providerDesc(provider)}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acct">{t("mobileMoney.accountNumber")}</Label>
              <Input
                id="acct"
                placeholder="+253 9..."
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="acctname">{t("mobileMoney.accountName")}</Label>
              <Input
                id="acctname"
                placeholder={t("mobileMoney.accountNamePlaceholder")}
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pin">{t("mobileMoney.enterPin", { provider: providerLabel(provider) })}</Label>
              <Input
                id="pin"
                type="password"
                inputMode="numeric"
                maxLength={6}
                placeholder="••••"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              />
              <p className="text-[11px] text-muted-foreground">
                {t("mobileMoney.demoSimulated")}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={link} disabled={linking}>
              {linking ? t("mobileMoney.linking") : t("mobileMoney.link")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
